import React, { useState, useEffect, useMemo, useRef } from 'react';
import NavBar from '../../components/layout/NavBar';
import Footer from '../../components/layout/Footer';
import HeroBanner from '../../components/loja/HeroBanner';
import FeaturesBar from '../../components/loja/FeaturesBar';
import CategoryNav from '../../components/loja/CategoryNav';
import FilterBar from '../../components/loja/FilterBar';
import ProductCard from '../../components/loja/ProductCard';
import ProductModal from '../../components/loja/ProductModal';
import CartDrawer from '../../components/loja/CartDrawer';
import FavoritesDrawer from '../../components/loja/FavoritesDrawer';
import { useLojaCart } from '../../context/LojaCartContext';
import LojaService from '../../services/Loja/LojaService';
import { ShoppingBag, Heart, Sparkles, AlertCircle } from 'lucide-react';

export default function LojaUrbView() {
  const { openCart, openFavorites, totalItemsCount, favorites } = useLojaCart();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [activeCategory, setActiveCategory] = useState('todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGender, setSelectedGender] = useState('Todos');
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [sortBy, setSortBy] = useState('relevance');

  // Modal de Detalhes
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const catalogRef = useRef(null);

  // Carrega categorias e produtos
  useEffect(() => {
    async function loadInitialData() {
      setLoading(true);
      try {
        const [cats, prods] = await Promise.all([
          LojaService.getCategories(),
          LojaService.getProducts()
        ]);
        setCategories(cats);
        setProducts(prods);
      } catch (err) {
        console.error('Erro ao carregar catálogo da Loja URB:', err);
      } finally {
        setLoading(false);
      }
    }
    loadInitialData();
  }, []);

  // Recarrega produtos conforme filtros
  useEffect(() => {
    let isMounted = true;
    async function filterProducts() {
      const prods = await LojaService.getProducts({
        category: activeCategory,
        search: searchQuery,
        gender: selectedGender,
        size: selectedSize,
        color: selectedColor,
        sort: sortBy
      });
      if (isMounted) {
        setProducts(prods);
      }
    }
    filterProducts();
    return () => { isMounted = false; };
  }, [activeCategory, searchQuery, selectedGender, selectedSize, selectedColor, sortBy]);

  // Contadores dinâmicos por categoria
  const productsCountByCat = useMemo(() => {
    const map = {};
    // Base total
    map['todos'] = products.length;
    categories.forEach(c => {
      if (c.slug !== 'todos') {
        const count = products.filter(p => p.category_id === c.id || p.category_id === `cat_${c.slug}`).length;
        map[c.slug] = count;
      }
    });
    return map;
  }, [categories, products]);

  const hasActiveFilters = Boolean(
    searchQuery || 
    selectedGender !== 'Todos' || 
    selectedSize || 
    selectedColor || 
    activeCategory !== 'todos'
  );

  const handleClearFilters = () => {
    setActiveCategory('todos');
    setSearchQuery('');
    setSelectedGender('Todos');
    setSelectedSize(null);
    setSelectedColor(null);
    setSortBy('relevance');
  };

  const scrollToCatalog = () => {
    if (catalogRef.current) {
      catalogRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleExplorarKits = () => {
    setActiveCategory('kits');
    scrollToCatalog();
  };

  const handleOpenProduct = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-urb-dark flex flex-col selection:bg-urb-primary selection:text-black">
      
      {/* Navegação Principal */}
      <NavBar />

      {/* Sub-Header da Loja Oficial com Ações Rápidas */}
      <div className="sticky top-16 z-30 bg-urb-dark/90 backdrop-blur-md border-b border-urb-border/80 px-4 sm:px-6 py-2.5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-urb-primary animate-ping" />
            <span className="text-xs font-black uppercase tracking-wider text-white">
              LOJA OFICIAL <span className="text-urb-primary">URBRACE</span>
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Botão Favoritos com Contador */}
            <button
              type="button"
              onClick={openFavorites}
              className="relative p-2 rounded-xl bg-urb-card-light hover:bg-white/10 text-white transition-colors cursor-pointer border border-urb-border"
              title="Meus Favoritos"
            >
              <Heart className={`w-4 h-4 ${favorites.length > 0 ? 'text-red-400 fill-red-400' : ''}`} />
              {favorites.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center px-1">
                  {favorites.length}
                </span>
              )}
            </button>

            {/* Botão Carrinho com Badge Neon */}
            <button
              type="button"
              onClick={openCart}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-urb-primary text-black font-extrabold text-xs uppercase tracking-wider hover:bg-urb-primary-hover shadow-[0_0_15px_rgba(138,230,0,0.3)] transition-all cursor-pointer"
            >
              <ShoppingBag className="w-4 h-4 stroke-[2.5]" />
              <span className="hidden sm:inline">CARRINHO</span>
              <span className="px-1.5 py-0.5 rounded-md bg-black text-urb-primary font-black text-[11px]">
                {totalItemsCount}
              </span>
            </button>
          </div>

        </div>
      </div>

      <main className="flex-1">
        {/* Hero Section */}
        <HeroBanner 
          onVerColecao={scrollToCatalog}
          onExplorarKits={handleExplorarKits}
        />

        {/* Barra de Vantagens e Confiança */}
        <FeaturesBar />

        {/* Seção Principal do Catálogo */}
        <section ref={catalogRef} id="catalogo" className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-8">
          
          {/* Cabeçalho da Seção */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-urb-border/50 pb-5">
            <div>
              <div className="flex items-center gap-2 text-urb-primary text-xs font-black uppercase tracking-widest mb-1">
                <Sparkles className="w-3.5 h-3.5" />
                <span>DROP PERFORMANCE // EQUIPAMENTOS OFICIAIS</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight uppercase">
                Coleção Completa URB
              </h2>
            </div>

            <div className="text-xs text-urb-text-muted">
              Mostrando <strong className="text-white">{products.length}</strong> {products.length === 1 ? 'produto' : 'produtos'}
            </div>
          </div>

          {/* Navegação de Categorias */}
          <CategoryNav
            categories={categories}
            activeCategory={activeCategory}
            onSelectCategory={setActiveCategory}
            productsCountByCat={productsCountByCat}
          />

          {/* Barra de Filtros e Busca */}
          <FilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedGender={selectedGender}
            onGenderChange={setSelectedGender}
            selectedSize={selectedSize}
            onSizeChange={setSelectedSize}
            selectedColor={selectedColor}
            onColorChange={setSelectedColor}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onClearFilters={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
          />

          {/* Grade de Produtos */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="rounded-2xl bg-urb-card border border-urb-border p-4 h-96 animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="p-12 text-center rounded-3xl bg-urb-card/40 border border-urb-border space-y-4">
              <div className="w-16 h-16 rounded-full bg-urb-dark flex items-center justify-center mx-auto text-urb-text-muted">
                <AlertCircle className="w-8 h-8 opacity-40" />
              </div>
              <h3 className="text-lg font-bold text-white">Nenhum produto encontrado</h3>
              <p className="text-xs text-urb-text-secondary max-w-sm mx-auto">
                Não encontramos produtos para os filtros selecionados. Tente limpar os filtros ou buscar por outros termos.
              </p>
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-5 py-2.5 rounded-xl bg-urb-primary text-black font-extrabold text-xs uppercase tracking-wider hover:bg-urb-primary-hover cursor-pointer"
              >
                Limpar Filtros
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onOpenQuickView={handleOpenProduct}
                />
              ))}
            </div>
          )}

        </section>
      </main>

      {/* Modal de Detalhes do Produto */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProduct(null);
        }}
      />

      {/* Gaveta do Carrinho */}
      <CartDrawer />

      {/* Gaveta de Favoritos */}
      <FavoritesDrawer onOpenProductModal={handleOpenProduct} />

      {/* Rodapé Oficial URBRACE */}
      <Footer />

    </div>
  );
}
